<template>
  <div class="ev-page">
    <div class="ev-header">
      <div class="ev-header__info">
        <div class="ev-header__icon ev-header__icon--conferencia">
          <i class="fa fa-check-square-o"></i>
        </div>
        <div>
          <h1 class="ev-header__title">Separacao de Vendas</h1>
          <span class="ev-header__sub">Pedidos do fluxo com separacao aguardando leitura de etiquetas</span>
        </div>
      </div>

      <div class="ev-header__controls">
        <div class="ev-refresh-control mr-3 d-none d-lg-flex">
          <div class="ev-refresh-info">
            <i class="fa fa-refresh mr-1" :class="{'fa-spin': carregando}"></i>
            <span>Auto-atualizacao: <strong>{{ refreshInterval }}s</strong></span>
          </div>
          <b-form-input
            v-model="refreshInterval"
            type="range"
            min="5"
            max="60"
            step="5"
            class="ev-refresh-slider"
            :disabled="carregando"
          />
        </div>

        <b-button variant="primary" pill class="px-3 font-weight-bold shadow-sm" @click="carregarDados" :disabled="carregando">
          <i class="fa fa-refresh mr-1" :class="{'fa-spin': carregando}"></i> Atualizar
        </b-button>
      </div>
    </div>

    <div class="ev-cards">
      <div class="ev-card ev-card--blue shadow-xs">
        <div class="ev-card__num">{{ pedidos.length }}</div>
        <div class="ev-card__label">Total de Pedidos</div>
      </div>
      <div class="ev-card ev-card--indigo shadow-xs">
        <div class="ev-card__num">{{ contarStatus('7') }}</div>
        <div class="ev-card__label">Aguardando</div>
      </div>
      <div class="ev-card ev-card--amber shadow-xs">
        <div class="ev-card__num">{{ contarStatus('8') }}</div>
        <div class="ev-card__label">Em Separacao</div>
      </div>
      <div class="ev-card ev-card--teal shadow-xs">
        <div class="ev-card__num font-weight-bold">{{ formatarQuantTotal(totalConferidoGeral) }}</div>
        <div class="ev-card__label">Total Separado (kg)</div>
      </div>
    </div>

    <div class="ev-filtros">
      <div class="ev-filtros__search shadow-xs">
        <span class="ev-filtros__icon"><i class="fa fa-search"></i></span>
        <input
          v-model="filtro"
          class="ev-filtros__input"
          placeholder="Filtrar por cliente, pedido ou vendedor..."
          @input="aplicarFiltros"
        />
      </div>
      <div class="ev-filtros__chips">
        <button
          class="ev-chip shadow-xs"
          :class="statusFiltro === null ? 'ev-chip--active' : ''"
          @click="setStatus(null)"
        >Todos ({{ pedidos.length }})</button>
        <button
          class="ev-chip ev-chip--indigo shadow-xs"
          :class="statusFiltro === '7' ? 'ev-chip--active' : ''"
          @click="setStatus('7')"
        >Aguardando ({{ contarStatus('7') }})</button>
        <button
          class="ev-chip ev-chip--amber shadow-xs"
          :class="statusFiltro === '8' ? 'ev-chip--active' : ''"
          @click="setStatus('8')"
        >Em Separacao ({{ contarStatus('8') }})</button>
      </div>
    </div>

    <div class="ev-table-card shadow-sm">
      <b-overlay :show="carregando" rounded="sm" opacity="0.6">
        <b-table
          :items="pedidosFiltrados"
          :fields="campos"
          hover
          responsive
          show-empty
          empty-text="Nenhum pedido elegivel para separacao"
          class="ev-table mb-0"
          thead-class="ev-thead"
          @row-clicked="onRowClicked"
          :tbody-tr-class="rowClass"
        >
          <template #cell(DETALHE)="row">
            <b-button variant="link" class="p-0 text-secondary" @click.stop="toggleDetalhes(row)">
              <i :class="row.detailsShowing ? 'fa fa-angle-up' : 'fa fa-angle-down'"></i>
            </b-button>
          </template>

          <template #cell(SEQUENCIA)="{ item }">
            <span class="ev-seq">#{{ item.SEQUENCIA }}</span>
          </template>

          <template #cell(DT_SOLICITA_CONFERENCIA)="{ item }">
            <span class="ev-date">{{ formatarData(item.DT_SOLICITA_CONFERENCIA || item.DT_PEDIDO) }}</span>
          </template>

          <template #cell(NOME_CLIENTE)="{ item }">
            <div class="ev-cliente">
              <div class="ev-cliente__avatar">{{ inicialCliente(item.NOME_CLIENTE) }}</div>
              <div>
                <div class="ev-cliente__nome">{{ item.NOME_CLIENTE || '-' }}</div>
                <div class="ev-cliente__cod" v-if="item.NOME_VENDEDOR">{{ item.NOME_VENDEDOR }}</div>
              </div>
            </div>
          </template>

          <template #cell(SITUACAO)="{ item }">
            <span class="ev-status" :class="situacaoClass(item.SITUACAO)">
              <span class="ev-status__dot"></span>
              {{ situacaoTexto(item.SITUACAO) }}
            </span>
          </template>

          <template #cell(PESO_PEDIDO)="{ item }">
            <span class="ev-num ev-num--gray">{{ formatarQuant(item.PESO_PEDIDO) }}</span>
          </template>

          <template #cell(PESO_CONFERIDO)="{ item }">
            <span class="ev-num" :class="Number(item.PESO_CONFERIDO) > 0 ? 'ev-num--green' : 'ev-num--gray'">
              {{ formatarQuant(item.PESO_CONFERIDO) }}
            </span>
          </template>

          <template #cell(PROGRESSO)="{ item }">
            <div class="ev-prog-wrap">
              <div class="ev-prog-bar">
                <div
                  class="ev-prog-fill"
                  :style="{ width: calcProgresso(item) + '%' }"
                  :class="progressoFillClass(calcProgresso(item))"
                ></div>
              </div>
              <span class="ev-prog-pct">{{ calcProgresso(item) }}%</span>
            </div>
          </template>

          <template #cell(ACOES)="{ item }">
            <div class="d-flex align-items-center gap-2">
              <button class="ev-btn-acao shadow-sm" :class="item.SITUACAO === '7' ? 'ev-btn-acao--green' : 'ev-btn-acao--amber'" @click.stop="abrirTerminal(item)">
                <i :class="item.SITUACAO === '7' ? 'fa fa-play' : 'fa fa-pencil'"></i>
                {{ item.SITUACAO === '7' ? 'Iniciar' : 'Continuar' }}
              </button>

              <button
                class="ev-btn-acao ev-btn-acao--blue shadow-sm"
                @click.stop="imprimirEspelho(item)"
                title="Imprimir Espelho da Separacao"
                :disabled="Number(item.PESO_CONFERIDO) === 0"
              >
                <i class="fa fa-print"></i>
              </button>

              <button
                class="ev-btn-acao ev-btn-acao--danger shadow-sm"
                @click.stop="devolverPedido(item)"
                title="Devolver para o Vendedor"
                :disabled="!podeDevolver(item)"
              >
                <i class="fa fa-undo"></i>
              </button>
            </div>
          </template>

          <template #row-details="row">
            <div class="p-3 bg-light border-top border-bottom">
              <div v-if="row.item.loadingItens" class="text-center p-3">
                <b-spinner small variant="primary" /> Carregando itens...
              </div>
              <div v-else>
                <div class="mb-3 d-flex justify-content-between align-items-center">
                  <h6 class="modal-section-title mb-0 font-weight-bold"><i class="fa fa-list mr-2 text-primary"></i>ITENS DO PEDIDO</h6>
                  <b-badge variant="soft-dark" pill>{{ (row.item.itens || []).length }} itens</b-badge>
                </div>

                <b-table
                  :items="row.item.itens"
                  :fields="camposItens"
                  small
                  hover
                  striped
                  borderless
                  class="bg-white rounded shadow-xs mb-0"
                  thead-class="bg-light font-xs text-muted"
                >
                  <template #cell(QUANT_PEDIDO)="d">
                    <span class="font-weight-bold">{{ formatarQuant(d.item.QUANT_PEDIDO) }}</span>
                  </template>
                  <template #cell(QUANT_CONFERIDA)="d">
                    <span class="font-weight-bold text-success">{{ formatarQuant(d.item.QUANT_CONFERIDA) }}</span>
                  </template>
                  <template #cell(PERCENT)="d">
                    <small class="font-weight-bold" :class="calcPctItem(d.item) >= 100 ? 'text-success' : 'text-warning'">
                      {{ calcPctItem(d.item) }}%
                    </small>
                  </template>
                </b-table>
              </div>
            </div>
          </template>
        </b-table>

        <div class="ev-footer" v-if="pedidosFiltrados.length > porPagina">
          <span class="ev-footer__info">
            Exibindo {{ Math.min(paginaAtual * porPagina, pedidosFiltrados.length) }} de {{ pedidosFiltrados.length }}
          </span>
          <b-pagination
            v-model="paginaAtual"
            :total-rows="pedidosFiltrados.length"
            :per-page="porPagina"
            size="sm"
            align="right"
            class="mb-0"
          />
        </div>
      </b-overlay>
    </div>

    <div class="ev-table-card shadow-sm mt-4">
      <div class="ev-history-header">
        <div>
          <h5 class="mb-0 text-primary font-weight-bold">
            <i class="fa fa-history mr-2"></i>Pedidos Separados Recentemente
          </h5>
          <small class="text-muted">Historico do dia</small>
        </div>
        <b-button variant="outline-primary" size="sm" pill @click="carregarHistoricoSeparados" :disabled="carregandoHistorico">
          <i class="fa fa-refresh mr-1" :class="{'fa-spin': carregandoHistorico}"></i> Atualizar
        </b-button>
      </div>
      <b-table
        :items="historicoSeparados"
        :fields="camposHistorico"
        :busy.sync="carregandoHistorico"
        hover
        responsive
        small
        show-empty
        empty-text="Nenhum pedido separado hoje"
        class="ev-table mb-0"
        thead-class="ev-thead"
      >
        <template #cell(SEQUENCIA)="{ item }">
          <span class="ev-seq">#{{ item.SEQUENCIA }}</span>
        </template>
        <template #cell(DT_CONFERENCIA_FIM)="{ item }">
          <span class="ev-date">{{ formatarDataHora(item.DT_CONFERENCIA_FIM) }}</span>
        </template>
        <template #cell(NOME_CLIENTE)="{ item }">
          <div class="font-weight-bold">{{ item.NOME_CLIENTE || '-' }}</div>
          <small class="text-muted">{{ item.NOME_VENDEDOR || '' }}</small>
        </template>
        <template #cell(PESO_CONFERIDO)="{ item }">
          <span class="ev-num ev-num--green">{{ formatarQuant(item.PESO_CONFERIDO) }}</span>
        </template>
        <template #cell(TOTAL_ETIQUETAS)="{ item }">
          <b-badge variant="success" pill>{{ item.TOTAL_ETIQUETAS || 0 }}</b-badge>
        </template>
      </b-table>
    </div>

    <conferencia-vendas-modal
      ref="terminalModal"
      :pedido-id="selectedPedidoId"
      @concluido="carregarDados"
    />
  </div>
</template>

<script>
import ConferenciaVendasModal from './ConferenciaVendasModal.vue'
import VendaExpedicaoReport from '@/utils/reports/VendaExpedicaoReport'

export default {
  name: 'ConferenciaVendasListagem',
  components: {
    ConferenciaVendasModal
  },
  data () {
    return {
      carregando: false,
      pedidos: [],
      pedidosFiltrados: [],
      historicoSeparados: [],
      filtro: '',
      statusFiltro: null,
      carregandoHistorico: false,
      paginaAtual: 1,
      porPagina: 20,
      selectedPedidoId: null,
      refreshInterval: 15,
      refreshTimer: null,
      campos: [
        { key: 'DETALHE', label: '', thStyle: { width: '40px' }, tdClass: 'text-center' },
        { key: 'SEQUENCIA', label: 'Pedido', sortable: true, thClass: 'ev-th' },
        { key: 'DT_SOLICITA_CONFERENCIA', label: 'Solicitacao', sortable: true, thClass: 'ev-th' },
        { key: 'NOME_CLIENTE', label: 'Cliente / Vendedor', sortable: true, thClass: 'ev-th' },
        { key: 'SITUACAO', label: 'Status', thClass: 'ev-th' },
        { key: 'PESO_PEDIDO', label: 'Peso Pedido', thClass: 'ev-th text-right', tdClass: 'ev-td text-right' },
        { key: 'PESO_CONFERIDO', label: 'Separado', thClass: 'ev-th text-right', tdClass: 'ev-td text-right' },
        { key: 'PROGRESSO', label: 'Progresso', thClass: 'ev-th' },
        { key: 'ACOES', label: '', thClass: 'ev-th', tdClass: 'ev-td' }
      ],
      camposHistorico: [
        { key: 'SEQUENCIA', label: 'Pedido', sortable: true },
        { key: 'DT_CONFERENCIA_FIM', label: 'Finalizado', sortable: true },
        { key: 'NOME_CLIENTE', label: 'Cliente / Vendedor', sortable: true },
        { key: 'PESO_CONFERIDO', label: 'Peso Separado', class: 'text-right' },
        { key: 'TOTAL_ETIQUETAS', label: 'Etiquetas', class: 'text-center' },
        { key: 'USUARIO_CONFERENCIA', label: 'Usuario', sortable: true }
      ],
      camposItens: [
        { key: 'ITEM', label: '#' },
        { key: 'COD_PRO', label: 'Cod' },
        { key: 'DESCRICAO', label: 'Produto' },
        { key: 'UNIDADE', label: 'Un' },
        { key: 'QUANT_PEDIDO', label: 'Pedida', class: 'text-right' },
        { key: 'QUANT_CONFERIDA', label: 'Conferida', class: 'text-right' },
        { key: 'PERCENT', label: '%', class: 'text-right' }
      ]
    }
  },
  computed: {
    totalConferidoGeral () {
      return this.pedidos.reduce((s, p) => s + Number(p.PESO_CONFERIDO || 0), 0)
    }
  },
  watch: {
    refreshInterval () {
      this.reiniciarTimer()
    }
  },
  created () {
    this.carregarDados()
    this.iniciarTimer()
  },
  beforeDestroy () {
    this.pararTimer()
  },
  methods: {
    async carregarPedidos () {
      if (this.carregando) return
      this.carregando = true
      try {
        const result = await this.$http.get('/api/protected/conferencia-vendas/pedidos-elegiveis')
        this.pedidos = result.data || []
        this.aplicarFiltros()
      } catch (err) {
        console.error('Erro ao carregar pedidos para conferencia', err)
      } finally {
        this.carregando = false
      }
    },
    async carregarHistoricoSeparados () {
      this.carregandoHistorico = true
      try {
        const result = await this.$http.get('/api/protected/conferencia-vendas/pedidos-separados-hoje')
        this.historicoSeparados = result.data || []
      } catch (err) {
        console.error('Erro ao carregar historico de pedidos separados', err)
      } finally {
        this.carregandoHistorico = false
      }
    },
    async carregarDados () {
      await Promise.all([
        this.carregarPedidos(),
        this.carregarHistoricoSeparados()
      ])
    },
    iniciarTimer () {
      this.pararTimer()
      this.refreshTimer = setInterval(() => {
        this.carregarDados()
      }, this.refreshInterval * 1000)
    },
    pararTimer () {
      if (this.refreshTimer) clearInterval(this.refreshTimer)
    },
    reiniciarTimer () {
      this.iniciarTimer()
    },
    aplicarFiltros () {
      let lista = [...this.pedidos]
      if (this.statusFiltro) lista = lista.filter(p => p.SITUACAO === this.statusFiltro)
      if (this.filtro.trim()) {
        const f = this.filtro.toLowerCase()
        lista = lista.filter(p =>
          String(p.SEQUENCIA).includes(f) ||
          (p.NOME_CLIENTE || '').toLowerCase().includes(f) ||
          (p.NOME_VENDEDOR || '').toLowerCase().includes(f)
        )
      }
      this.pedidosFiltrados = lista
    },
    onRowClicked (item) {
      this.selectedPedidoId = item.SEQUENCIA
    },
    rowClass (item) {
      if (!item) return ''
      return item.SEQUENCIA === this.selectedPedidoId ? 'ev-tr-selected' : 'ev-tr'
    },
    setStatus (valor) {
      this.statusFiltro = this.statusFiltro === valor ? null : valor
      this.aplicarFiltros()
    },
    contarStatus (valor) {
      return this.pedidos.filter(p => p.SITUACAO === valor).length
    },
    calcProgresso (pedido) {
      const p = Number(pedido.PESO_PEDIDO) || 0
      const e = Number(pedido.PESO_CONFERIDO) || 0
      if (p === 0) return 0
      return Math.min(100, Math.round((e / p) * 100))
    },
    calcPctItem (item) {
      const p = Number(item.QUANT_PEDIDO) || 0
      const e = Number(item.QUANT_CONFERIDA) || 0
      if (p === 0) return 0
      return Math.min(100, Math.round((e / p) * 100))
    },
    progressoFillClass (pct) {
      if (pct >= 100) return 'ev-prog-fill--green'
      if (pct >= 50) return 'ev-prog-fill--amber'
      return 'ev-prog-fill--blue'
    },
    situacaoTexto (s) {
      return { '7': 'AGUARDANDO SEPARACAO', '8': 'EM SEPARACAO' }[String(s || '').trim()] || s
    },
    situacaoClass (s) {
      return { '7': 'ev-status--indigo', '8': 'ev-status--amber' }[String(s || '').trim()] || ''
    },
    formatarQuant (v) {
      return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })
    },
    formatarQuantTotal (v) {
      return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    },
    formatarData (v) {
      if (!v) return '-'
      return new Date(v).toLocaleDateString('pt-BR')
    },
    formatarDataHora (v) {
      if (!v) return '-'
      return new Date(v).toLocaleString('pt-BR')
    },
    inicialCliente (nome) {
      if (!nome) return '?'
      return nome.trim().charAt(0).toUpperCase()
    },
    async abrirTerminal (pedido) {
      this.selectedPedidoId = pedido.SEQUENCIA
      if (String(pedido.SITUACAO || '').trim() === '7') {
        try {
          await this.$http.post('/api/protected/separacao-vendas/iniciar', {
            pedidoId: pedido.SEQUENCIA,
            usuario: window.localStorage.getItem('login')
          })
          this.$set(pedido, 'SITUACAO', '8')
          this.aplicarFiltros()
        } catch (err) {
          this.$bvToast.toast((err && err.response && err.response.data) || err || 'Nao foi possivel iniciar a separacao.', { title: 'Aviso', variant: 'warning', autoHideDelay: 3500 })
          return
        }
      }
      this.$nextTick(() => {
        this.$refs.terminalModal.abrir()
      })
    },
    podeDevolver (pedido) {
      return ['7', '8'].includes(String(pedido.SITUACAO || '').trim()) && Number(pedido.TOTAL_ETIQUETAS || 0) === 0
    },
    async devolverPedido (pedido) {
      if (!this.podeDevolver(pedido)) return

      const confirmar = await this.$bvModal.msgBoxConfirm(`Devolver o pedido ${pedido.N_VENDA || pedido.SEQUENCIA} para o vendedor?`, {
        title: 'Devolver Pedido',
        okTitle: 'SIM, DEVOLVER',
        cancelTitle: 'CANCELAR',
        okVariant: 'warning',
        centered: true
      })
      if (!confirmar) return

      try {
        await this.$http.post('/api/protected/conferencia-vendas/devolver-separacao', {
          pedidoId: pedido.SEQUENCIA,
          usuario: window.localStorage.getItem('login')
        })
        await this.carregarDados()
        this.$bvToast.toast('Pedido devolvido para o vendedor.', { title: 'Aviso', variant: 'success', autoHideDelay: 2500 })
      } catch (err) {
        this.$bvToast.toast((err && err.response && err.response.data) || err || 'Nao foi possivel devolver o pedido.', { title: 'Aviso', variant: 'warning', autoHideDelay: 3500 })
      }
    },
    async imprimirEspelho (pedido) {
      VendaExpedicaoReport.imprimir(pedido, this.$http, this.$store)
    },
    async toggleDetalhes (row) {
      if (row.detailsShowing) {
        row.toggleDetails()
        return
      }

      if (!row.item.itens) {
        this.$set(row.item, 'loadingItens', true)
        try {
          const response = await this.$http.get(`/api/protected/conferencia-vendas/itens-pedido/${row.item.SEQUENCIA}`)
          this.$set(row.item, 'itens', response.data.itens || [])
        } catch (err) {
          this.$bvToast.toast('Erro ao carregar itens', { variant: 'danger' })
        } finally {
          this.$set(row.item, 'loadingItens', false)
        }
      }
      row.toggleDetails()
    }
  }
}
</script>

<style scoped>
.ev-page {
  padding: 20px 24px;
  font-family: 'Segoe UI', 'Inter', sans-serif;
  background: #f4f6f9;
  min-height: calc(100vh - 60px);
}
.ev-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.08);
}
.ev-header__info { display: flex; align-items: center; gap: 14px; }
.ev-header__icon {
  width: 46px;
  height: 46px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #fff;
  flex-shrink: 0;
}
.ev-header__icon--conferencia {
  background: linear-gradient(135deg, #0ea5e9, #6366f1);
  box-shadow: 0 4px 14px rgba(14,165,233,0.3);
}
.ev-header__title { font-size: 1.25rem; font-weight: 700; color: #1e293b; margin: 0; }
.ev-header__sub { color: #64748b; font-size: 0.82rem; }
.ev-header__controls { display: flex; align-items: center; }
.ev-refresh-control { display: flex; flex-direction: column; min-width: 150px; }
.ev-refresh-info { font-size: 0.75rem; color: #64748b; margin-bottom: 4px; display: flex; justify-content: space-between; }
.ev-refresh-slider { height: 4px; padding: 0; }
.ev-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}
.ev-card {
  background: #fff;
  border-radius: 12px;
  padding: 16px 20px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.07);
  border-left: 4px solid transparent;
  transition: transform 0.15s;
}
.ev-card:hover { transform: translateY(-2px); }
.ev-card--blue { border-color: #3b82f6; }
.ev-card--indigo { border-color: #6366f1; }
.ev-card--amber { border-color: #f59e0b; }
.ev-card--teal { border-color: #14b8a6; }
.ev-card__num { font-size: 1.8rem; font-weight: 700; color: #1e293b; line-height: 1; }
.ev-card__label { color: #64748b; font-size: 0.78rem; margin-top: 4px; text-transform: uppercase; letter-spacing: 0.04em; }
.ev-filtros { display: flex; align-items: center; gap: 14px; margin-bottom: 16px; flex-wrap: wrap; }
.ev-filtros__search { position: relative; flex: 1; min-width: 220px; }
.ev-filtros__icon { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; }
.ev-filtros__input {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 8px 12px 8px 36px;
  font-size: 0.875rem;
  background: #fff;
  color: #1e293b;
  outline: none;
  transition: all 0.2s;
}
.ev-filtros__input:focus { border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99,102,241,0.12); }
.ev-filtros__chips { display: flex; gap: 8px; flex-wrap: wrap; }
.ev-chip {
  border: 1px solid #e2e8f0;
  background: #fff;
  border-radius: 20px;
  padding: 5px 14px;
  font-size: 0.8rem;
  cursor: pointer;
  color: #64748b;
  font-weight: 500;
  transition: all 0.15s;
  white-space: nowrap;
}
.ev-chip:hover { border-color: #c7d2fe; color: #4f46e5; }
.ev-chip--active { background: #4f46e5 !important; border-color: #4f46e5 !important; color: #fff !important; }
.ev-table-card { background: #fff; border-radius: 12px; box-shadow: 0 1px 6px rgba(0,0,0,0.07); overflow: hidden; }
.ev-history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-bottom: 1px solid #f1f5f9;
  background: #fff;
}
.ev-thead th {
  background: #f8fafc !important;
  color: #475569 !important;
  font-size: 0.73rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.06em !important;
  font-weight: 600 !important;
  border-bottom: 1px solid #e2e8f0 !important;
  padding: 10px 14px !important;
}
.ev-table td { vertical-align: middle; padding: 12px 14px; border-color: #f1f5f9; }
.ev-tr { cursor: pointer; }
.ev-tr-selected { background: #eef2ff !important; cursor: pointer; }
.ev-seq { font-weight: 800; color: #4f46e5; }
.ev-date { color: #64748b; font-size: 0.85rem; }
.ev-cliente { display: flex; align-items: center; gap: 10px; min-width: 0; }
.ev-cliente__avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #e0e7ff;
  color: #4338ca;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  flex-shrink: 0;
}
.ev-cliente__nome { font-weight: 700; color: #1e293b; max-width: 280px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ev-cliente__cod { color: #94a3b8; font-size: 0.75rem; }
.ev-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
}
.ev-status__dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; }
.ev-status--indigo { background: #eef2ff; color: #4f46e5; }
.ev-status--amber { background: #fffbeb; color: #d97706; }
.ev-num { font-weight: 800; font-size: 0.95rem; }
.ev-num--gray { color: #475569; }
.ev-num--green { color: #059669; }
.ev-prog-wrap { display: flex; align-items: center; gap: 8px; min-width: 120px; }
.ev-prog-bar { flex: 1; height: 8px; background: #e2e8f0; border-radius: 8px; overflow: hidden; }
.ev-prog-fill { height: 100%; border-radius: 8px; transition: width 0.3s; }
.ev-prog-fill--blue { background: #3b82f6; }
.ev-prog-fill--amber { background: #f59e0b; }
.ev-prog-fill--green { background: #10b981; }
.ev-prog-pct { font-size: 0.75rem; color: #64748b; width: 36px; text-align: right; }
.ev-btn-acao {
  border: 0;
  border-radius: 8px;
  padding: 7px 12px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  transition: all 0.15s;
}
.ev-btn-acao:disabled { opacity: 0.45; cursor: not-allowed; }
.ev-btn-acao:hover:not(:disabled) { transform: translateY(-1px); }
.ev-btn-acao--green { background: #10b981; }
.ev-btn-acao--amber { background: #f59e0b; }
.ev-btn-acao--blue { background: #3b82f6; min-width: 38px; }
.ev-btn-acao--danger { background: #ef4444; min-width: 38px; }
.ev-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-top: 1px solid #f1f5f9;
  background: #f8fafc;
}
.ev-footer__info { font-size: 0.8rem; color: #64748b; }
.gap-2 { gap: 8px; }
.font-xs { font-size: 0.75rem; }
@media (max-width: 768px) {
  .ev-header { flex-direction: column; align-items: stretch; gap: 14px; }
  .ev-header__controls { justify-content: flex-end; }
}
</style>



// WEBPACK FOOTER //
// src/views/expedicao/conferencia/ConferenciaVendasListagem.vue